import { b as private_env } from "./shared-server.js";
import { l as logger } from "./logger.js";
import { resolveSrv } from "dns/promises";
import { ReadPreference, MongoClient } from "mongodb";
import { p as parseMongoUri, a as mongoHostKey } from "./mongoUri.js";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
function sanitizeMongoUri(connectionString) {
  try {
    const parsed = parseMongoUri(connectionString);
    if (!parsed.username && !parsed.password) {
      return connectionString;
    }
    const tail = `${parsed.pathname}${parsed.search ? `?${parsed.search}` : ""}`;
    return `${parsed.protocol}//***:***@${parsed.hostsRaw}${tail}`;
  } catch {
    return "***";
  }
}
function buildDirectConnectionUri(connectionString, host) {
  const parsed = parseMongoUri(connectionString);
  const params = new URLSearchParams(parsed.search);
  params.set("directConnection", "true");
  if (parsed.protocol === "mongodb+srv:" || params.has("tls") || params.has("ssl")) {
    params.set("tls", "true");
  }
  const auth = parsed.username || parsed.password ? `${encodeURIComponent(parsed.username)}${parsed.password ? `:${encodeURIComponent(parsed.password)}` : ""}@` : "";
  const pathname = parsed.pathname || "/";
  return `mongodb://${auth}${host}${pathname}?${params.toString()}`;
}
const DEFAULT_HOSTS = private_env.MONGOKU_DEFAULT_HOST ? private_env.MONGOKU_DEFAULT_HOST.split(";") : ["localhost:27017"];
const DATABASE_FILE = private_env.MONGOKU_DATABASE_FILE || path.join(os.homedir(), ".mongoku.db");
class HostsManager {
  _hosts = /* @__PURE__ */ new Map();
  // path -> _id
  async load() {
    let first = false;
    try {
      await fs.promises.stat(DATABASE_FILE);
    } catch {
      first = true;
    }
    if (!first) {
      await this._loadFromFile();
    }
    if (first || this._hosts.size === 0) {
      for (const hostname of DEFAULT_HOSTS) {
        this._hosts.set(hostname, this._generateId());
      }
      await this._saveToFile();
    }
  }
  async _loadFromFile() {
    const content = await fs.promises.readFile(DATABASE_FILE, "utf8");
    const lines = content.trim().split("\n").filter((line) => line.trim());
    const newHosts = /* @__PURE__ */ new Map();
    for (const line of lines) {
      const host = JSON.parse(line);
      if (host && typeof host.path === "string") {
        const id = host._id || this._generateId();
        newHosts.set(host.path, id);
      }
    }
    this._hosts = newHosts;
  }
  async _saveToFile() {
    const lines = Array.from(this._hosts).map(([hostPath, id]) => JSON.stringify({ path: hostPath, _id: id }));
    await fs.promises.writeFile(DATABASE_FILE, lines.join("\n") + "\n", "utf8");
  }
  _generateId() {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
  async getHosts() {
    return Array.from(this._hosts).map(([hostPath, id]) => ({ path: hostPath, _id: id }));
  }
  getHost(name) {
    return this._hosts.get(name);
  }
  async add(hostPath) {
    let id = this._hosts.get(hostPath);
    if (!id) {
      id = this._generateId();
      this._hosts.set(hostPath, id);
    }
    await this._saveToFile();
    return id;
  }
  async removeById(id) {
    for (const [hostPath, hostId] of this._hosts) {
      if (hostId === id) {
        this._hosts.delete(hostPath);
        break;
      }
    }
    await this._saveToFile();
  }
}
async function getCollectionJson(collection, type) {
  const stats = {
    size: 0,
    count: 0,
    avgObjSize: 0,
    storageSize: 0,
    capped: false,
    nindexes: 0,
    totalIndexSize: 0,
    indexSizes: {}
  };
  if (type !== "view") {
    const agg = await collection.aggregate([
      {
        $collStats: {
          storageStats: {},
          count: {}
        }
      }
    ]).next().catch(() => null);
    if (agg) {
      stats.size = agg.storageStats.size;
      stats.count = agg.storageStats.count;
      stats.avgObjSize = Math.round(agg.storageStats.size / agg.storageStats.count);
      stats.storageSize = agg.storageStats.storageSize;
      stats.capped = agg.storageStats.capped;
      stats.nindexes = agg.storageStats.nindexes;
      stats.totalIndexSize = agg.storageStats.totalIndexSize;
      stats.indexSizes = agg.storageStats.indexSizes;
    }
  }
  let indexes = [];
  if (type !== "view") {
    try {
      const indexList = await collection.listIndexes().toArray();
      indexes = indexList.map((index) => ({
        name: index.name,
        key: index.key,
        size: stats.indexSizes[index.name] || 0
      }));
    } catch {
      indexes = Object.entries(stats.indexSizes).map(([name, size]) => ({
        name,
        size
      }));
    }
  }
  return {
    name: collection.collectionName,
    size: (stats.storageSize ?? 0) + (stats.totalIndexSize ?? 0),
    dataSize: stats.size,
    count: stats.count,
    avgObjSize: stats.avgObjSize ?? 0,
    storageSize: stats.storageSize ?? 0,
    capped: stats.capped,
    nIndexes: stats.nindexes,
    totalIndexSize: stats.totalIndexSize ?? 0,
    indexSizes: stats.indexSizes,
    indexes
  };
}
async function extractNodesFromConnectionString(connectionString) {
  const parsed = parseMongoUri(connectionString);
  if (parsed.protocol === "mongodb+srv:") {
    const hostname = parsed.hosts[0]?.split(":")[0];
    if (!hostname) {
      throw new Error("No hostname found in SRV connection string");
    }
    const srvRecords = await resolveSrv(`_mongodb._tcp.${hostname}`);
    return srvRecords.sort((a, b) => a.priority - b.priority || b.weight - a.weight).map((record) => `${record.name}:${record.port}`).sort();
  }
  if (parsed.protocol === "mongodb:") {
    if (parsed.hosts.length === 0) {
      throw new Error("No hosts found in connection string");
    }
    return [...parsed.hosts].sort();
  }
  throw new TypeError(`Unsupported protocol: ${parsed.protocol}`);
}
class MongoClientWithMappings extends MongoClient {
  url;
  mappings = {};
  indexes = {};
  _id;
  name;
  constructor(url, _id, name, readPreference) {
    super(url, readPreference ? { readPreference } : {});
    this.url = url;
    this._id = _id;
    this.name = name;
  }
  async getMappings(dbName, collectionName, opts) {
    if (opts?.forceRefresh || !this.mappings[dbName]?.[collectionName]) {
      const mappings = await this.db(dbName).collection("mongoku.mappings").findOne({ _id: collectionName });
      this.mappings[dbName] ??= {};
      this.mappings[dbName][collectionName] = mappings?.mappings ?? {};
    }
    return this.mappings[dbName][collectionName];
  }
  clearMappingsCache(dbName, collectionName) {
    delete this.mappings[dbName][collectionName];
  }
  async getIndexes(dbName, collectionName, opts) {
    if (opts?.forceRefresh || !this.indexes[dbName]?.[collectionName]) {
      const indexList = await this.db(dbName).collection(collectionName).listIndexes().toArray();
      this.indexes[dbName] ??= {};
      this.indexes[dbName][collectionName] = indexList.map((index) => ({
        name: index.name,
        key: index.key
      }));
    }
    return this.indexes[dbName][collectionName];
  }
  setIndexes(dbName, collectionName, indexes) {
    this.indexes[dbName] ??= {};
    this.indexes[dbName][collectionName] = indexes;
  }
  clearIndexesCache(dbName, collectionName) {
    delete this.indexes[dbName]?.[collectionName];
  }
  /**
   * Check if a field has an index (as the first key in the index)
   */
  async hasIndexOnField(dbName, collectionName, field) {
    const indexes = await this.getIndexes(dbName, collectionName);
    return indexes.some((index) => {
      const keys = Object.keys(index.key);
      return keys[0] === field;
    });
  }
}
class MongoConnections {
  /**
   * Todo: better system where we can have mutiple servers with same hostname, and labels for each server that
   * would be displayed in the UI instead of the hostname.
   */
  clients = /* @__PURE__ */ new Map();
  // _id -> MongoClientWithMappings
  clientIds = /* @__PURE__ */ new Map();
  // hostname -> _id
  hostsManager;
  countTimeout = parseInt(private_env.MONGOKU_COUNT_TIMEOUT, 10) || 3e4;
  queryTimeout = private_env.MONGOKU_QUERY_TIMEOUT ? parseInt(private_env.MONGOKU_QUERY_TIMEOUT, 10) : void 0;
  excludedDatabases;
  readPreference;
  constructor() {
    this.hostsManager = new HostsManager();
    const excludeEnv = private_env.MONGOKU_EXCLUDE_DATABASES || "";
    this.excludedDatabases = new Set(
      excludeEnv.split(",").map((db) => db.trim()).filter((db) => db.length > 0)
    );
    const readPrefMode = private_env.MONGOKU_READ_PREFERENCE;
    if (readPrefMode) {
      let tags;
      if (private_env.MONGOKU_READ_PREFERENCE_TAGS) {
        try {
          tags = JSON.parse(private_env.MONGOKU_READ_PREFERENCE_TAGS);
        } catch (err) {
          logger.error("Failed to parse MONGOKU_READ_PREFERENCE_TAGS:", err);
        }
      }
      this.readPreference = tags ? new ReadPreference(readPrefMode, tags) : new ReadPreference(readPrefMode);
      logger.log(`Read preference configured: ${readPrefMode}${tags ? ` with tags ${JSON.stringify(tags)}` : ""}`);
    }
  }
  async initialize() {
    await this.hostsManager.load();
    const hosts = await this.hostsManager.getHosts();
    for (const host of hosts) {
      const urlStr = host.path.startsWith("mongodb") ? host.path : `mongodb://${host.path}`;
      try {
        const hostname = mongoHostKey(urlStr) || host.path;
        if (!this.clients.has(hostname)) {
          const client = new MongoClientWithMappings(urlStr, host._id, hostname, this.readPreference);
          this.clients.set(host._id, client);
          this.clientIds.set(hostname, host._id);
        }
      } catch (err) {
        logger.error(`Failed to parse URL for host ${host.path}:`, err);
      }
    }
  }
  getClient(name) {
    const clientId = this.clientIds.get(name) || this.clientIds.get(`${name}:27017`);
    if (!clientId) {
      throw new Error(`Client not found: ${name}`);
    }
    const client = this.clients.get(clientId);
    if (!client) {
      throw new Error(`Client not found: ${name}`);
    }
    return client;
  }
  listClients() {
    return Array.from(this.clients.values()).map((client) => ({
      name: client.name,
      _id: client._id,
      client
    }));
  }
  getCountTimeout() {
    return this.countTimeout;
  }
  getQueryTimeout() {
    return this.queryTimeout;
  }
  filterDatabases(databases) {
    if (this.excludedDatabases.size === 0) {
      return databases;
    }
    return databases.filter((db) => !this.excludedDatabases.has(db.name));
  }
  async addServer(hostPath) {
    const id = await this.hostsManager.add(hostPath);
    const urlStr = hostPath.startsWith("mongodb") ? hostPath : `mongodb://${hostPath}`;
    try {
      const hostname = mongoHostKey(urlStr) || hostPath;
      if (!this.clients.has(hostname)) {
        const client = new MongoClientWithMappings(urlStr, id, hostname, this.readPreference);
        this.clients.set(id, client);
        this.clientIds.set(hostname, id);
      }
    } catch (err) {
      logger.error(`Failed to parse URL for host ${hostPath}:`, err);
      throw err;
    }
  }
  async removeServer(name) {
    const clientKey = this.clientIds.has(name) ? name : `${name}:27017`;
    const clientId = this.clientIds.get(clientKey);
    if (!clientId) {
      throw new Error(`Server not found: ${name}`);
    }
    await this.hostsManager.removeById(clientId);
    this.clients.get(clientId)?.close().catch((err) => logger.error(`Error closing client ${name}:`, err));
    this.clients.delete(clientId);
    this.clientIds.delete(clientKey);
  }
  async reconnectClient(id) {
    const oldClient = this.clients.get(id);
    if (!oldClient) {
      throw new Error(`Client not found: ${id}`);
    }
    oldClient.close().catch((err) => logger.error(`Error closing old client ${id}:`, err));
    const newClient = new MongoClientWithMappings(oldClient.url, id, oldClient.name, this.readPreference);
    this.clients.set(id, newClient);
    await newClient.connect();
  }
  /**
   * Get the list of nodes from a server's connection string
   */
  async getServerNodes(serverId) {
    const client = this.getClient(serverId);
    return extractNodesFromConnectionString(client.url);
  }
  /**
   * Fetch index stats from a specific node using directConnection
   */
  async getIndexStatsFromNode(serverId, node, database, collection) {
    const client = this.getClient(serverId);
    const directUri = buildDirectConnectionUri(client.url, node);
    let nodeClient = null;
    try {
      nodeClient = new MongoClient(directUri);
      await nodeClient.connect();
      const coll = nodeClient.db(database).collection(collection);
      const statsResult = await coll.aggregate([{ $indexStats: {} }]).toArray();
      const indexStats = Object.fromEntries(
        statsResult.map((stat) => [
          stat.name,
          {
            ops: stat.accesses?.ops || 0,
            since: stat.accesses?.since || /* @__PURE__ */ new Date(),
            host: stat.host || node
          }
        ])
      );
      return indexStats;
    } finally {
      if (nodeClient) {
        await nodeClient.close();
      }
    }
  }
  /**
   * Return the shard key for every sharded collection in a database, in a single
   * query against `config.collections`. Read-only.
   *
   * Returns a map of collectionName -> shard key. Collections that are not
   * sharded (or on non-sharded clusters) are simply absent from the map.
   * Designed to be cheap enough to call once for a whole collection list.
   */
  async getDatabaseShardKeys(serverId, database) {
    const client = this.getClient(serverId);
    const configDb = client.db("config");
    const result = {};
    try {
      const prefix = `${database}.`;
      const docs = await configDb.collection("collections").find({
        _id: { $regex: `^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}` }
      }).project({ _id: 1, key: 1, dropped: 1 }).toArray();
      for (const doc of docs) {
        if (doc.dropped || !doc.key) {
          continue;
        }
        const name = doc._id.slice(prefix.length);
        result[name] = doc.key;
      }
    } catch (err) {
      logger.error("Error reading config.collections for shard keys:", err);
    }
    return result;
  }
  /**
   * Gather sharding information for a collection.
   *
   * Everything here is read-only: it reads the `config` database
   * (config.collections / config.chunks / config.shards) and runs `$collStats`.
   * On a non-sharded (standalone / replica set) deployment, the `config`
   * collections won't exist and we return `shardingEnabled: false`.
   */
  async getShardingInfo(serverId, database, collection) {
    const client = this.getClient(serverId);
    const ns = `${database}.${collection}`;
    const info = {
      shardingEnabled: false,
      sharded: false,
      shardKey: null,
      hashed: false,
      unique: false,
      totalChunks: 0,
      shards: [],
      balancerEnabled: true,
      warning: null
    };
    const configDb = client.db("config");
    let shardDocs = [];
    try {
      shardDocs = await configDb.collection("shards").find({}).toArray();
    } catch (err) {
      logger.error("Error reading config.shards (not a sharded cluster?):", err);
      return info;
    }
    if (shardDocs.length === 0) {
      return info;
    }
    info.shardingEnabled = true;
    let collConfig = null;
    try {
      collConfig = await configDb.collection("collections").findOne({ _id: ns });
    } catch (err) {
      logger.error("Error reading config.collections:", err);
      info.warning = "Could not read config.collections";
    }
    const perShard = /* @__PURE__ */ new Map();
    try {
      const statsResults = await client.db(database).collection(collection).aggregate([{ $collStats: { storageStats: {} } }]).toArray();
      for (const stat of statsResults) {
        const shardId = stat.shard || shardDocs[0]?._id || "unknown";
        perShard.set(shardId, {
          documents: stat.storageStats?.count ?? null,
          size: stat.storageStats?.size ?? null
        });
      }
    } catch (err) {
      logger.error("Error reading $collStats for sharding info:", err);
    }
    if (collConfig && !collConfig.dropped && collConfig.key) {
      info.sharded = true;
      info.shardKey = collConfig.key;
      info.hashed = Object.values(collConfig.key).some((v) => v === "hashed");
      info.unique = !!collConfig.unique;
      info.balancerEnabled = !collConfig.noBalance;
      try {
        let chunkFilter = { ns };
        if (collConfig.uuid !== void 0) {
          chunkFilter = { uuid: collConfig.uuid };
        }
        const chunkCounts = await configDb.collection("chunks").aggregate([{ $match: chunkFilter }, { $group: { _id: "$shard", count: { $sum: 1 } } }]).toArray();
        const chunkMap = new Map(chunkCounts.map((c) => [c._id, c.count]));
        info.totalChunks = chunkCounts.reduce((acc, c) => acc + c.count, 0);
        info.shards = shardDocs.map((shard) => ({
          shardId: shard._id,
          host: shard.host,
          chunks: chunkMap.get(shard._id) ?? 0,
          documents: perShard.get(shard._id)?.documents ?? null,
          size: perShard.get(shard._id)?.size ?? null
        }));
      } catch (err) {
        logger.error("Error reading config.chunks:", err);
        info.warning = "Could not read chunk distribution";
        info.shards = shardDocs.map((shard) => ({
          shardId: shard._id,
          host: shard.host,
          chunks: 0,
          documents: perShard.get(shard._id)?.documents ?? null,
          size: perShard.get(shard._id)?.size ?? null
        }));
      }
    } else {
      info.sharded = false;
      info.shards = [...perShard.entries()].map(([shardId, stats]) => {
        const shard = shardDocs.find((s) => s._id === shardId);
        return {
          shardId,
          host: shard?.host ?? shardId,
          chunks: 0,
          documents: stats.documents,
          size: stats.size
        };
      });
    }
    return info;
  }
}
let mongoConnections = null;
let initPromise = null;
async function getMongo() {
  if (!mongoConnections) {
    if (!initPromise) {
      initPromise = (async () => {
        mongoConnections = new MongoConnections();
        await mongoConnections.initialize();
        return mongoConnections;
      })();
    }
    return initPromise;
  }
  return mongoConnections;
}
export {
  getCollectionJson as a,
  getMongo as g,
  sanitizeMongoUri as s
};
